/**
 * AIChatMessage — renders a single chat message bubble.
 *
 * Assistant messages are rendered through react-markdown + remark-gfm so
 * the LLM's Markdown output (bold, italic, tables, links, headings,
 * inline + fenced code, ordered + unordered lists, GitHub Flavored
 * Markdown extensions like tables and task lists) lays out properly
 * instead of showing as raw asterisks and pipes.
 *
 * Why react-markdown over a hand-rolled parser:
 *   - structural XSS safety: react-markdown emits React nodes; there's
 *     no dangerouslySetInnerHTML in this path, so a hostile markdown
 *     string cannot inject script tags or event handlers.
 *   - GFM tables, task lists, autolinks, strikethrough via remark-gfm
 *     with one plugin declaration.
 *   - streaming-friendly: re-renders cleanly on each chunk arrival;
 *     unclosed structures (a half-written table row, an unfinished
 *     code fence) degrade gracefully instead of breaking the parser.
 *
 * Component overrides below carry the dark-theme palette and keep
 * tables horizontally scrollable so wide content (the analyze_invoice_rules
 * tool's findings, the McDaniels setup sheet, etc.) never blows out
 * the bubble.
 */
import React, { useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../../../../packages/shared/src/types/aiChat';
import CopyMessageButton from './CopyMessageButton';

interface AIChatMessageProps {
  message: ChatMessage;
}

function formatTimestamp(date: Date): string {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Dark-theme component overrides for react-markdown.
// Inline styles (matches the rest of this component's pattern; no CSS module
// or styled-components in this folder).
const mono = '"SF Mono", "Fira Code", "Cascadia Code", monospace';

const markdownComponents = {
  p: ({ children }: { children?: React.ReactNode }) => <div style={{ lineHeight: '1.6', margin: '0 0 8px 0' }}>{children}</div>,
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--tc-text-placeholder, #EEEEEE)', margin: '14px 0 8px' }}>{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--tc-text-placeholder, #EEEEEE)', margin: '12px 0 6px' }}>{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--tc-text-placeholder, #EEEEEE)', margin: '10px 0 4px' }}>{children}</h3>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong style={{ fontWeight: 600, color: 'var(--tc-text-placeholder, #EEEEEE)' }}>{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em style={{ fontStyle: 'italic', color: 'var(--tc-text-secondary, #CCCCCC)' }}>{children}</em>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--tc-text-selected, #6BA6F3)', textDecoration: 'underline' }}
    >
      {children}
    </a>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul style={{ paddingLeft: 20, margin: '4px 0 8px', lineHeight: '1.6' }}>{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol style={{ paddingLeft: 22, margin: '4px 0 8px', lineHeight: '1.6' }}>{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  blockquote: ({ children }: { children?: React.ReactNode }) => (
    <blockquote
      style={{
        borderLeft: '3px solid var(--tc-border-lighter, #444444)',
        margin: '6px 0',
        padding: '2px 10px',
        color: 'var(--tc-text-secondary, #CCCCCC)',
        background: 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))',
      }}
    >
      {children}
    </blockquote>
  ),
  hr: () => <hr style={{ border: 'none', borderTop: '1px solid var(--tc-border-light, #333333)', margin: '10px 0' }} />,
  // Tables — wrap in a scrollable container so wide tables (e.g. the
  // McDaniels setup sheet) don't blow out the message bubble.
  table: ({ children }: { children?: React.ReactNode }) => (
    <div style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table
        style={{
          borderCollapse: 'collapse',
          fontSize: '12px',
          width: '100%',
          minWidth: 'max-content',
        }}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead style={{ background: 'var(--tc-background-highlight, #252525)' }}>{children}</thead>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr style={{ borderBottom: '1px solid var(--tc-border-light, #333333)' }}>{children}</tr>
  ),
  th: ({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) => (
    <th
      style={{
        textAlign: 'left',
        padding: '6px 10px',
        color: 'var(--tc-text-secondary, #CCCCCC)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </th>
  ),
  td: ({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) => (
    <td
      style={{
        padding: '6px 10px',
        color: 'var(--tc-text-secondary, #CCCCCC)',
        verticalAlign: 'top',
        ...style,
      }}
    >
      {children}
    </td>
  ),
  // Code: distinguish inline from fenced via the `className` react-markdown
  // sets (`language-xyz`) on fenced blocks. Inline code = no className.
  code: ({ className, children, ...rest }: { className?: string; children?: React.ReactNode; inline?: boolean }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <code
          style={{
            backgroundColor: 'var(--tc-overlay-washStrong, rgba(255, 255, 255, 0.08))',
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: '0.9em',
            fontFamily: mono,
            color: 'var(--tc-text-secondary, #CCCCCC)',
          }}
          {...rest}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={className}
        style={{
          display: 'block',
          fontFamily: mono,
          fontSize: '12px',
          lineHeight: '1.5',
          color: 'var(--tc-text-placeholder, #EEEEEE)',
        }}
        {...rest}
      >
        {children}
      </code>
    );
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre
      style={{
        backgroundColor: 'var(--tc-background-secondary, #0B0B0B)',
        border: '1px solid var(--tc-border-light, #333333)',
        borderRadius: 6,
        padding: 12,
        margin: '8px 0',
        overflowX: 'auto',
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily: mono,
        color: 'var(--tc-text-placeholder, #EEEEEE)',
      }}
    >
      {children}
    </pre>
  ),
};

const AIChatMessage: React.FC<AIChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';
  // The rendered bubble is the source for the text/html clipboard flavour, so
  // a paste into a document matches what is on screen.
  const renderedRef = useRef<HTMLDivElement | null>(null);
  // Nothing worth copying until there is content, and copying mid-stream
  // would take half a sentence.
  const canCopy = message.content.trim().length > 0 && !message.isStreaming;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '16px',
        padding: '0 16px',
      }}
    >
      <div
        style={{
          maxWidth: '88%',
          padding: '10px 14px',
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          backgroundColor: isUser ? 'var(--tc-background-elevated, #333333)' : 'var(--tc-background-tertiary, #1A1A1A)',
          border: isUser ? 'none' : '1px solid var(--tc-border-light, #333333)',
          color: 'var(--tc-text-placeholder, #EEEEEE)',
          fontSize: '13px',
          lineHeight: '1.5',
          wordBreak: 'break-word',
        }}
      >
        {isUser ? (
          // User input is shown verbatim. We deliberately don't render the
          // user side as markdown — operator-typed pipes/asterisks should
          // appear as-typed, not be reinterpreted.
          <span ref={renderedRef as React.RefObject<HTMLSpanElement>} style={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </span>
        ) : (
          <>
            <div ref={renderedRef}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as never}>
                {message.content}
              </ReactMarkdown>
            </div>
            {message.isStreaming && (
              <span
                style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '16px',
                  backgroundColor: 'var(--tc-status-info, #2196F3)',
                  marginLeft: '2px',
                  verticalAlign: 'text-bottom',
                  animation: 'aiCursorBlink 1s step-end infinite',
                }}
              />
            )}
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          fontSize: '11px',
          color: 'var(--tc-text-tertiary, #999999)',
          marginTop: '4px',
          padding: '0 4px',
          flexDirection: isUser ? 'row-reverse' : 'row',
        }}
      >
        <span>{formatTimestamp(message.timestamp)}</span>
        {canCopy && (
          <CopyMessageButton
            markdown={message.content}
            renderedRef={renderedRef}
            label={isUser ? 'Copy your message' : "Copy the assistant's reply"}
          />
        )}
      </div>
      <style>{`
        @keyframes aiCursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default AIChatMessage;
